import React, { useState } from 'react';
import { FileText, Trash2, FilePlus, CheckCircle, Clock, Pencil, Mail } from 'lucide-react';
import { printInvoice } from '../../utils/printInvoice';

export const invoiceToPrintable = (inv) => ({
  order_id: inv.invoice_no,
  created_at: inv.created_at,
  status: 'wholesale',
  customer_name: inv.customer_name,
  customer_email: inv.customer_email,
  customer_phone: inv.customer_phone,
  billing_address: inv.billing_address,
  shipping_address: null,
  items: inv.items,
  subtotal: inv.subtotal,
  discount: inv.discount || 0,
  shipping: inv.shipping || 0,
  total: inv.total,
  payment_display: 'Direct Transfer / EFT',
  payment_paid: inv.payment_paid,
  payment_terms: inv.payment_terms,
  bank_details: inv.bank_details,
});

const WholesaleTab = ({ invoices, password, onRefresh, onCreate, onEdit }) => {
  const [busy, setBusy] = useState(null);
  const [emailState, setEmailState] = useState({});

  const emailInvoice = async (inv) => {
    let email = inv.customer_email;
    if (!email) {
      email = window.prompt(`No email saved for ${inv.invoice_no}. Enter the customer's email address:`);
      if (!email || !email.trim()) return;
      email = email.trim();
    }
    setEmailState(prev => ({ ...prev, [inv.invoice_no]: 'sending' }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wholesale/invoices/${encodeURIComponent(inv.invoice_no)}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, email })
      });
      if (!res.ok) throw new Error();
      setEmailState(prev => ({ ...prev, [inv.invoice_no]: 'sent' }));
      setTimeout(() => setEmailState(prev => ({ ...prev, [inv.invoice_no]: null })), 4000);
    } catch {
      setEmailState(prev => ({ ...prev, [inv.invoice_no]: null }));
      window.alert(`Failed to email invoice ${inv.invoice_no}. Please try again.`);
    }
  };

  const togglePaid = async (inv) => {
    setBusy(inv.invoice_no);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wholesale/invoices/${encodeURIComponent(inv.invoice_no)}/paid`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, payment_paid: !inv.payment_paid })
      });
      onRefresh();
    } finally {
      setBusy(null);
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no}? This cannot be undone.`)) return;
    setBusy(inv.invoice_no);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wholesale/invoices/${encodeURIComponent(inv.invoice_no)}?password=${encodeURIComponent(password)}`, { method: 'DELETE' });
      onRefresh();
    } finally {
      setBusy(null);
    }
  };

  const outstanding = invoices.filter(i => !i.payment_paid).reduce((s, i) => s + i.total, 0);

  return (
    <div data-testid="wholesale-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Wholesale</h1>
          {invoices.length > 0 && (
            <p className="text-sm text-foreground/60 mt-1" data-testid="wholesale-outstanding">
              Outstanding: <strong className={outstanding > 0 ? 'text-amber-600' : 'text-green-600'}>${outstanding.toFixed(2)}</strong> across {invoices.filter(i => !i.payment_paid).length} unpaid invoice{invoices.filter(i => !i.payment_paid).length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button onClick={onCreate} className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="wholesale-create-invoice-button">
          <FilePlus className="w-4 h-4" /><span className="text-sm">Create Invoice</span>
        </button>
      </div>

      {invoices.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-foreground/70">No wholesale invoices yet</p>
          <p className="text-sm text-foreground/50 mt-1">Create your first invoice for a trade customer</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <div key={inv.invoice_no} className="p-5 rounded-2xl border border-border bg-background/50" data-testid={`wholesale-invoice-${inv.invoice_no}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-foreground">{inv.invoice_no}</h3>
                    <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${inv.payment_paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`wholesale-status-${inv.invoice_no}`}>
                      {inv.payment_paid ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      <span>{inv.payment_paid ? 'Paid' : 'Payment Due'}</span>
                    </span>
                  </div>
                  <p className="text-sm text-foreground/60 mt-1">
                    {inv.customer_name} · {new Date(inv.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {' · '}{inv.items.reduce((s, i) => s + i.quantity, 0)} units
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-foreground mr-2">${inv.total.toFixed(2)}</span>
                  <button onClick={() => togglePaid(inv)} disabled={busy === inv.invoice_no}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${inv.payment_paid ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                    data-testid={`toggle-paid-${inv.invoice_no}`}>
                    {busy === inv.invoice_no ? '...' : inv.payment_paid ? 'Mark as Due' : 'Mark as Paid'}
                  </button>
                  <button onClick={() => onEdit(inv)}
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-border text-foreground/70 hover:text-primary hover:border-primary transition-colors"
                    data-testid={`edit-wholesale-${inv.invoice_no}`}>
                    <Pencil className="w-4 h-4" /><span className="text-xs font-medium">Edit</span>
                  </button>
                  <button onClick={() => printInvoice(invoiceToPrintable(inv))}
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-border text-foreground/70 hover:text-primary hover:border-primary transition-colors"
                    data-testid={`print-wholesale-${inv.invoice_no}`}>
                    <FileText className="w-4 h-4" /><span className="text-xs font-medium">Invoice</span>
                  </button>
                  <button onClick={() => emailInvoice(inv)} disabled={emailState[inv.invoice_no] === 'sending'}
                    title={inv.customer_email ? `Email PDF to ${inv.customer_email}` : 'Email PDF (no email saved — you\'ll be asked for one)'}
                    className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-60 ${emailState[inv.invoice_no] === 'sent' ? 'border-green-300 text-green-700 bg-green-50' : 'border-border text-foreground/70 hover:text-primary hover:border-primary'}`}
                    data-testid={`email-wholesale-${inv.invoice_no}`}>
                    {emailState[inv.invoice_no] === 'sent' ? <CheckCircle className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                    <span className="text-xs font-medium">{emailState[inv.invoice_no] === 'sending' ? 'Sending...' : emailState[inv.invoice_no] === 'sent' ? 'Sent' : 'Email'}</span>
                  </button>
                  <button onClick={() => deleteInvoice(inv)} disabled={busy === inv.invoice_no}
                    className="p-2 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50" data-testid={`delete-wholesale-${inv.invoice_no}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WholesaleTab;
